import React from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding, // My Apartment icon
  faStore, // Property Marketplace icon
  faHouseChimneyWindow, // Alternative: more inviting 'My Apartment'
  faShop, // Alternative: more modern 'Marketplace'
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify"; // Import ToastContainer
import axios from "axios";

const FirstScreen = () => {
  // Retrieve user data from localStorage
  const userDataString = localStorage.getItem("user");
  const userData = JSON.parse(userDataString);
  const userName = userData?.fullName || "User";
  const userId = userData.unique;
  const token = localStorage.getItem("token");

  const navigate = useNavigate();

  // Determine the greeting based on the time of day
  const hours = new Date().getHours();
  const greeting =
    hours < 12
      ? "Good Morning"
      : hours < 18
      ? "Good Afternoon"
      : "Good Evening";

  // Handlers
  const handleMyApartmentClick = () => {
    navigate("/user/myapartment");
  };

  const handleMarketplaceClick = async () => {
    try {
      // Show a temporary loading toast
      toast.info("Checking your marketplace access...", { autoClose: 2000 });

      const response = await axios.get(
        `https://backend.inyumba.co.ke/iNyumba/user/agencyviewingfee/checkagencyhuntingstatus/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const message = response.data.response;

      if (message === "You have already paid. No need to pay again.") {
        toast.success("Access granted! Happy house hunting.");
        navigate("/user/propertymarketplace");
      } else if (message === "Please pay the propertyhuntingfee.") {
        toast.info("House hunting fee required to access the marketplace.");
        navigate("/user/agencyviewingfee");
      } else {
        toast.error("Unexpected response. Please try again.");
        // Consider a more specific redirect or action here if an unexpected state occurs
        navigate("/user/myapartment");
      }
    } catch (error) {
      console.error("Error checking agency hunting status:", error);
      toast.error("Failed to check access. Please try again later.");
      // Fallback in case of network or server error
      navigate("/user/myapartment");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6 md:p-12 lg:p-16 relative overflow-hidden">
      {/* Background Shapes/Glows (Decorative) */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute top-1/4 right-1/4 w-60 h-60 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>

      <div className="relative z-10 w-full max-w-4xl text-center">
        {/* Greeting Section */}
        <div className="mb-14 animate-fade-in-down">
          <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight drop-shadow-lg">
            {greeting},
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">
              {userName}!
            </span>
          </h1>
          <p className="mt-4 text-xl text-gray-700 font-medium tracking-wide">
            What would you like to explore today? ✨
          </p>
        </div>

        {/* Grid Container for Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {/* My Apartment Card */}
          <div
            className="flex flex-col items-center justify-center p-10 bg-white rounded-3xl shadow-2xl cursor-pointer
                       transform transition-all duration-300 ease-in-out hover:-translate-y-2 hover:shadow-3xl-custom
                       border-b-4 border-indigo-400 group relative overflow-hidden animate-fade-in-left"
            onClick={handleMyApartmentClick}
            aria-label="Go to My Apartment Dashboard"
          >
            {/* Background shimmer effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-blue-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-3xl"></div>

            <div className="relative z-10 flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 rounded-full mb-6 shadow-lg
                           bg-gradient-to-br from-indigo-100 to-indigo-200 text-indigo-700
                           group-hover:text-white group-hover:from-indigo-400 group-hover:to-indigo-500
                           transition-all duration-300">
              <FontAwesomeIcon icon={faHouseChimneyWindow} className="text-5xl sm:text-6xl group-hover:animate-bounce-y" />
            </div>
            <h2 className="relative z-10 text-3xl font-extrabold text-gray-800 group-hover:text-gray-900 transition-colors duration-300">
              My Apartment
            </h2>
            <p className="relative z-10 text-md text-gray-600 mt-2 group-hover:text-gray-700 transition-colors duration-300">
              Manage your current residence
            </p>
          </div>

          {/* Property Marketplace Card */}
          <div
            className="flex flex-col items-center justify-center p-10 bg-white rounded-3xl shadow-2xl cursor-pointer
                       transform transition-all duration-300 ease-in-out hover:-translate-y-2 hover:shadow-3xl-custom
                       border-b-4 border-emerald-400 group relative overflow-hidden animate-fade-in-right"
            onClick={handleMarketplaceClick}
            aria-label="Explore Property Marketplace"
          >
            {/* Background shimmer effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-green-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-3xl"></div>

            <div className="relative z-10 flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 rounded-full mb-6 shadow-lg
                           bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700
                           group-hover:text-white group-hover:from-emerald-400 group-hover:to-emerald-500
                           transition-all duration-300">
              <FontAwesomeIcon icon={faShop} className="text-5xl sm:text-6xl group-hover:animate-bounce-y" />
            </div>
            <h2 className="relative z-10 text-3xl font-extrabold text-gray-800 group-hover:text-gray-900 transition-colors duration-300">
              Property Marketplace
            </h2>
            <p className="relative z-10 text-md text-gray-600 mt-2 group-hover:text-gray-700 transition-colors duration-300">
              Find your next home
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstScreen;